import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Wand2,
  Loader2,
  Coins,
  X,
  TreePine,
  Gem,
  Hexagon,
  Grid3X3,
  Layers,
  Check,
  Bath,
  ShowerHead,
  Droplets,
  CircleDot,
  UtensilsCrossed,
  ChefHat,
} from 'lucide-react';
import { Rnd } from 'react-rnd';
import Moveable from 'react-moveable';
import type { OnDrag, OnDragStart, OnResize, OnResizeStart, OnRotate, OnRotateStart } from 'react-moveable';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  FloorplanState,
  FloorplanFlooring,
  FloorplanTextHandling,
  FloorplanConfig,
  FloorplanAreaGroup,
  FloorplanFixture,
  FixtureType,
} from '@/types';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';
import { FloorplanAnalyzePanel } from '@/components/floorplan/FloorplanAnalyzePanel';
import { FixtureSymbolSvg } from '@/components/floorplan/FixtureSymbolSvg';
import { RotationControl } from '@/components/floorplan/RotationControl';
import { Slider } from '@/components/ui/slider';
import type { FixtureSymbolKey } from '@/lib/fixtureSymbols';
import { fixtureToFramePx, framePxToFixture, type FixtureFramePx } from '@/lib/floorplanFixtureFrame';
import { useAppSettings } from '@/hooks/useAppSettings';

// ============================================================================
// Constants
// ============================================================================

const FLOORING_OPTIONS: {
  id: FloorplanFlooring;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  { id: 'wood', label: 'Holz', description: 'Eiche, warme Brauntöne', icon: TreePine },
  { id: 'parquet', label: 'Parkett', description: 'Fischgrät, Honig-Eiche', icon: Layers },
  { id: 'stone', label: 'Stein', description: 'Hellgrauer Marmor', icon: Gem },
  { id: 'tile', label: 'Fliesen', description: 'Weiße Keramik', icon: Grid3X3 },
  { id: 'carpet', label: 'Teppich', description: 'Neutrales Beige', icon: Hexagon },
];

const FLOORING_LABEL_MAP: Record<FloorplanFlooring, string> = {
  wood: 'Holz',
  parquet: 'Parkett',
  stone: 'Stein',
  tile: 'Fliesen',
  carpet: 'Teppich',
};

const AREA_GROUPS: { id: FloorplanAreaGroup; label: string }[] = [
  { id: 'living', label: 'Wohnbereiche' },
  { id: 'kitchen', label: 'Küche' },
  { id: 'bathroom', label: 'Bad' },
  { id: 'outdoor', label: 'Balkon / Terrasse' },
];

// Sinnvolle Bodenbeläge je Bereich — keine Kombinationen wie Teppich auf Balkon
const AREA_FLOORING_OPTIONS: Record<FloorplanAreaGroup, FloorplanFlooring[]> = {
  living: ['wood', 'parquet', 'tile', 'carpet'],
  kitchen: ['tile', 'stone', 'parquet', 'wood'],
  bathroom: ['tile', 'stone'],
  outdoor: ['stone', 'tile', 'wood'],
};

const FIXTURE_PALETTE: {
  type: FixtureType;
  label: string;
  displayLabel: string;
  color: string;
  icon: React.ElementType;
  defaultW: number;
  defaultH: number;
}[] = [
  { type: 'shower', label: 'DUSCHE', displayLabel: 'Dusche', color: 'rgba(59, 130, 246, 0.5)', icon: ShowerHead, defaultW: 0.08, defaultH: 0.08 },
  { type: 'bathtub', label: 'BADEWANNE', displayLabel: 'Badewanne', color: 'rgba(16, 185, 129, 0.5)', icon: Bath, defaultW: 0.12, defaultH: 0.06 },
  { type: 'sink', label: 'WASCHBECKEN', displayLabel: 'Waschbecken', color: 'rgba(168, 85, 247, 0.5)', icon: Droplets, defaultW: 0.05, defaultH: 0.04 },
  { type: 'toilet', label: 'WC', displayLabel: 'WC', color: 'rgba(236, 72, 153, 0.5)', icon: CircleDot, defaultW: 0.04, defaultH: 0.05 },
  { type: 'kitchen_counter', label: 'KUCHENZEILE', displayLabel: 'Küchenzeile', color: 'rgba(234, 88, 12, 0.5)', icon: UtensilsCrossed, defaultW: 0.15, defaultH: 0.04 },
  { type: 'kitchen_island', label: 'KOCHINSEL', displayLabel: 'Kochinsel', color: 'rgba(234, 179, 8, 0.5)', icon: ChefHat, defaultW: 0.10, defaultH: 0.08 },
];

// ============================================================================
// Props
// ============================================================================

interface FloorplanEditorViewProps {
  projectId: string;
  floorplanState: FloorplanState;
  onFloorplanStateUpdate: (updates: Partial<FloorplanState>) => void;
  onGenerate: (annotatedStoragePath?: string) => void;
  onNavigateToReview: () => void;
  onBack: () => void;
  credits: number;
  floorplanUnits: number;
  onBuyCredits: () => void;
  // Admin-only in-process Prompt-Tuning-Panel (Vision-Analyst).
  isAdmin?: boolean;
  onGenerateWithPrompt?: (finalPromptOverride: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export const FloorplanEditorView: React.FC<FloorplanEditorViewProps> = ({
  projectId,
  floorplanState,
  onFloorplanStateUpdate,
  onGenerate,
  onNavigateToReview,
  onBack,
  floorplanUnits,
  onBuyCredits,
  isAdmin,
  onGenerateWithPrompt,
}) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  // Selection model (useSymbols branch only): drives on-canvas resize-handle visibility
  // + the right-sidebar "Ausgewählter Einbau" properties panel.
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);
  // react-rnd/re-resizable render the drag body + resize handles as siblings; releasing
  // a drag or resize gesture fires a native `click` that bubbles to the canvas container's
  // deselect handler. We track actual MOVEMENT (onDrag/onResize fire only once the pointer
  // has moved, never on a plain click-without-drag) rather than gesture start, so a bare
  // select-click never sets this. The flag is cleared via a `setTimeout(…, 0)` scheduled in
  // onDragStop/onResizeStop — not synchronously — so the trailing native `click` (which fires
  // before the timeout callback runs) still observes `true` and skips deselecting.
  const didInteractRef = useRef(false);
  // react-moveable fires drag/resize/rotate START+END around a bare click on its
  // control surface (no move in between). `didInteractRef` (set on any gesture START
  // for the moveable branch) guards the container deselect so a bare click never
  // deselects the fixture; `didMoveRef` (set only once the pointer actually moves)
  // gates whether the gesture-END persists geometry, so a bare click writes no no-op
  // state churn. Both are cleared after the gesture via setTimeout(…, 0).
  const didMoveRef = useRef(false);

  // Live geometry mutated by react-moveable during a drag/resize/rotate gesture.
  // Seeded from the selected fixture on each gesture start; persisted to state
  // (via updateFixture) on gesture end. Not React state — mutating it must not
  // re-render mid-gesture (moveable owns the inline style until the gesture ends).
  const gestureFrame = useRef<FixtureFramePx>({ translate: [0, 0], width: 0, height: 0, rotate: 0 });
  // react-moveable snapshots the target's rect on mount/gesture and does NOT observe
  // external style changes. When the properties panel (Breite/Höhe/Rotation) edits the
  // selected fixture, React re-renders the symbol div with a new transform but the
  // moveable control box keeps its stale rect → box and symbol diverge. We hold a ref
  // and call updateRect() whenever the selected fixture's geometry changes outside a
  // gesture, so the box re-reads the target. (No re-render fires mid-gesture, so this
  // never interferes with a live drag/resize/rotate.)
  const moveableRef = useRef<Moveable>(null);

  const config = floorplanState.config;

  // Feature flag: sanitary vector symbols in palette + preview (admin/live split).
  // `isAdmin` prop already mirrors useAuth().isAdmin from the parent (App.tsx),
  // so it's reused here instead of calling useAuth() again.
  const { settings: appSettings } = useAppSettings();
  const useSymbols = isAdmin
    ? appSettings.floorplan_fixture_symbols_admin
    : appSettings.floorplan_fixture_symbols_live;

  // ========================================================================
  // Container Size Tracking (for Rnd coordinate conversion)
  // ========================================================================

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // ========================================================================
  // Config Update Helpers
  // ========================================================================

  const updateConfig = useCallback(
    (updates: Partial<FloorplanConfig>) => {
      onFloorplanStateUpdate({
        config: { ...config, ...updates },
      });
    },
    [config, onFloorplanStateUpdate]
  );

  // ========================================================================
  // Per-Area Flooring Helpers
  // ========================================================================

  const getAreaFlooring = useCallback(
    (group: FloorplanAreaGroup): FloorplanFlooring => {
      return config.areaFlooring?.[group] ?? config.flooring;
    },
    [config.areaFlooring, config.flooring]
  );

  const setAreaFlooring = useCallback(
    (group: FloorplanAreaGroup, value: FloorplanFlooring) => {
      const current = config.areaFlooring ?? {
        living: config.flooring,
        kitchen: config.flooring,
        bathroom: config.flooring,
        outdoor: config.flooring,
      };
      updateConfig({
        areaFlooring: { ...current, [group]: value },
      });
    },
    [config.areaFlooring, config.flooring, updateConfig]
  );

  // ========================================================================
  // Fixture CRUD
  // ========================================================================

  const addFixture = useCallback(
    (type: FixtureType) => {
      const palette = FIXTURE_PALETTE.find((p) => p.type === type);
      if (!palette) return;

      const newFixture: FloorplanFixture = {
        id: `fixture-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        type,
        label: palette.label,
        x: 0.5 - palette.defaultW / 2,
        y: 0.5 - palette.defaultH / 2,
        width: palette.defaultW,
        height: palette.defaultH,
        color: palette.color,
      };

      updateConfig({
        fixtures: [...(config.fixtures || []), newFixture],
      });
      // Select the freshly placed fixture so the properties panel + on-canvas
      // handles are immediately available (useSymbols branch only).
      if (useSymbols) {
        setSelectedFixtureId(newFixture.id);
      }
    },
    [config.fixtures, updateConfig, useSymbols]
  );

  const updateFixture = useCallback(
    (fixtureId: string, updates: Partial<FloorplanFixture>) => {
      updateConfig({
        fixtures: (config.fixtures || []).map((f) =>
          f.id === fixtureId ? { ...f, ...updates } : f
        ),
      });
    },
    [config.fixtures, updateConfig]
  );

  const removeFixture = useCallback(
    (fixtureId: string) => {
      updateConfig({
        fixtures: (config.fixtures || []).filter((f) => f.id !== fixtureId),
      });
    },
    [config.fixtures, updateConfig]
  );

  // Selected fixture (useSymbols branch only) + its palette metadata for the
  // right-sidebar properties panel.
  const selectedFixture = useSymbols
    ? (config.fixtures || []).find((f) => f.id === selectedFixtureId)
    : undefined;
  const selectedFixturePalette = selectedFixture
    ? FIXTURE_PALETTE.find((p) => p.type === selectedFixture.type)
    : undefined;

  // Keep the moveable control box aligned when the panel sliders (Breite/Höhe/Rotation)
  // change the selected fixture's geometry — moveable won't notice the target's transform
  // changing on its own. Runs only on real state changes, never during a live gesture.
  useEffect(() => {
    moveableRef.current?.updateRect();
  }, [
    selectedFixture?.x,
    selectedFixture?.y,
    selectedFixture?.width,
    selectedFixture?.height,
    selectedFixture?.rotation,
    containerSize.width,
    containerSize.height,
  ]);

  // ========================================================================
  // Generate
  // ========================================================================

  // Use extracted unit image if available, otherwise the original upload
  const displayImageUrl = floorplanState.extractedUnitUrl || floorplanState.previewUrl;
  // Effective source floorplan used for 2D generation (mirrors useFloorplanWorkflow.handleGenerate2D).
  const analyzeSourcePath = floorplanState.extractedUnitPath || floorplanState.originalStoragePath;

  const handleGenerate = useCallback(() => {
    if (!displayImageUrl || !floorplanState.imageDbId) return;
    onGenerate();
  }, [displayImageUrl, floorplanState.imageDbId, onGenerate]);

  const isGenerating = floorplanState.is2dGenerating;
  const has2d = !!floorplanState.generated2dPath;
  // Pricing v4: the first generation per floorplan consumes 1 Grundriss-Einheit;
  // regenerations and the 3D view are free afterwards.
  const consumesUnit = !has2d;
  const hasQuota = !consumesUnit || floorplanUnits >= 1;

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Einbauten konfigurieren
            </h1>
            <p className="text-sm text-muted-foreground">
              Feste Einbauten und Bodenbelag
            </p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5">
          <Coins className="w-3.5 h-3.5" />
          {floorplanUnits} Grundriss{floorplanUnits !== 1 ? 'e' : ''}
        </Badge>
      </div>

      <FloorplanStepIndicator currentStep="editor" />

      {/* Main Content: Two Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Panel: Image + Labels + Fixtures */}
        <div className="lg:col-span-3 space-y-4">
          <Card>
            <CardContent className="p-4">
              <div
                ref={imageContainerRef}
                className="relative rounded-lg overflow-hidden bg-muted/30 border select-none"
                onClick={() => {
                  if (didInteractRef.current) return;
                  setSelectedFixtureId(null);
                }}
              >
                {displayImageUrl && (
                  <img
                    src={displayImageUrl}
                    alt="Grundriss"
                    className="w-full h-auto"
                    draggable={false}
                  />
                )}

                {/* Fixtures: symbol branch → transform-positioned div (react-moveable);
                    colored-box branch → react-rnd (unchanged). */}
                {containerSize.width > 0 && containerSize.height > 0 && config.fixtures?.map((fixture) => {
                  if (useSymbols) {
                    const frame = fixtureToFramePx(fixture, containerSize);
                    const selected = selectedFixtureId === fixture.id;
                    return (
                      <div
                        key={fixture.id}
                        data-fixture-id={fixture.id}
                        className={`absolute cursor-move rounded-sm ${
                          selected ? '' : 'border border-border/40'
                        }`}
                        style={{
                          left: 0,
                          top: 0,
                          width: `${frame.width}px`,
                          height: `${frame.height}px`,
                          transform: `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`,
                          zIndex: 10,
                        }}
                        onMouseDown={() => setSelectedFixtureId(fixture.id)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FixtureSymbolSvg
                          symbolKey={fixture.type as FixtureSymbolKey}
                          rotationDeg={0}
                          className="w-full h-full"
                        />
                        {/* Deletion for a selected symbol is via the "Löschen" button in the
                            "Ausgewählter Einbau" panel — an in-symbol X button sits on the
                            react-moveable target, which captures pointerdown for its drag
                            gesture and would swallow / fight the click. */}
                      </div>
                    );
                  }
                  return (
                    <Rnd
                      key={fixture.id}
                      position={{
                        x: fixture.x * containerSize.width,
                        y: fixture.y * containerSize.height,
                      }}
                      size={{
                        width: fixture.width * containerSize.width,
                        height: fixture.height * containerSize.height,
                      }}
                      bounds="parent"
                      minWidth={30}
                      minHeight={30}
                      onDrag={() => {
                        didInteractRef.current = true;
                      }}
                      onDragStop={(_e, d) => {
                        updateFixture(fixture.id, {
                          x: d.x / containerSize.width,
                          y: d.y / containerSize.height,
                        });
                        setTimeout(() => {
                          didInteractRef.current = false;
                        }, 0);
                      }}
                      onResize={() => {
                        didInteractRef.current = true;
                      }}
                      onResizeStop={(_e, _dir, ref, _delta, position) => {
                        updateFixture(fixture.id, {
                          x: position.x / containerSize.width,
                          y: position.y / containerSize.height,
                          width: ref.offsetWidth / containerSize.width,
                          height: ref.offsetHeight / containerSize.height,
                        });
                        setTimeout(() => {
                          didInteractRef.current = false;
                        }, 0);
                      }}
                      style={{ zIndex: 10 }}
                      className="group"
                    >
                      <div
                        className="w-full h-full flex items-center justify-center rounded border-2 cursor-move"
                        style={{
                          backgroundColor: fixture.color,
                          borderColor: fixture.color.replace(/[\d.]+\)$/, '1)'),
                        }}
                      >
                        <span className="text-xs font-bold text-black select-none pointer-events-none" style={{ textShadow: '0 0 4px rgba(255,255,255,0.95), 0 0 8px rgba(255,255,255,0.8)' }}>
                          {fixture.label}
                        </span>
                        <button
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFixture(fixture.id);
                          }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </Rnd>
                  );
                })}

                {/* react-moveable overlay for the selected symbol (symbol branch only).
                    Draggable + resizable + rotatable; the control box + handles rotate
                    WITH the element. Gesture events mutate the target's inline style live;
                    each gesture-end persists normalized geometry via updateFixture. */}
                {useSymbols && selectedFixtureId && containerSize.width > 0 && (
                  <Moveable
                    key={selectedFixtureId}
                    ref={moveableRef}
                    target={`[data-fixture-id="${selectedFixtureId}"]`}
                    rootContainer={imageContainerRef.current || undefined}
                    draggable
                    resizable
                    rotatable
                    origin={false}
                    keepRatio={false}
                    throttleDrag={0}
                    throttleResize={0}
                    throttleRotate={0}
                    onDragStart={({ set }: OnDragStart) => {
                      const f = selectedFixture;
                      if (!f) return;
                      didInteractRef.current = true;
                      didMoveRef.current = false;
                      gestureFrame.current = fixtureToFramePx(f, containerSize);
                      set(gestureFrame.current.translate);
                    }}
                    onDrag={({ target, beforeTranslate }: OnDrag) => {
                      didMoveRef.current = true;
                      gestureFrame.current.translate = [beforeTranslate[0], beforeTranslate[1]];
                      (target as HTMLElement).style.transform =
                        `translate(${beforeTranslate[0]}px, ${beforeTranslate[1]}px) rotate(${gestureFrame.current.rotate}deg)`;
                    }}
                    onResizeStart={({ setOrigin, dragStart }: OnResizeStart) => {
                      const f = selectedFixture;
                      if (!f) return;
                      didInteractRef.current = true;
                      didMoveRef.current = false;
                      gestureFrame.current = fixtureToFramePx(f, containerSize);
                      setOrigin(['%', '%']);
                      if (dragStart) dragStart.set(gestureFrame.current.translate);
                    }}
                    onResize={({ target, width, height, drag }: OnResize) => {
                      didMoveRef.current = true;
                      const t = drag.beforeTranslate;
                      gestureFrame.current.width = width;
                      gestureFrame.current.height = height;
                      gestureFrame.current.translate = [t[0], t[1]];
                      const el = target as HTMLElement;
                      el.style.width = `${width}px`;
                      el.style.height = `${height}px`;
                      el.style.transform =
                        `translate(${t[0]}px, ${t[1]}px) rotate(${gestureFrame.current.rotate}deg)`;
                    }}
                    onRotateStart={({ set }: OnRotateStart) => {
                      const f = selectedFixture;
                      if (!f) return;
                      didInteractRef.current = true;
                      didMoveRef.current = false;
                      gestureFrame.current = fixtureToFramePx(f, containerSize);
                      set(gestureFrame.current.rotate);
                    }}
                    onRotate={({ target, beforeRotate }: OnRotate) => {
                      didMoveRef.current = true;
                      gestureFrame.current.rotate = beforeRotate;
                      const [tx, ty] = gestureFrame.current.translate;
                      (target as HTMLElement).style.transform =
                        `translate(${tx}px, ${ty}px) rotate(${beforeRotate}deg)`;
                    }}
                    onDragEnd={() => {
                      if (didMoveRef.current) {
                        updateFixture(selectedFixtureId, framePxToFixture(gestureFrame.current, containerSize));
                      }
                      setTimeout(() => {
                        didInteractRef.current = false;
                      }, 0);
                    }}
                    onResizeEnd={() => {
                      if (didMoveRef.current) {
                        updateFixture(selectedFixtureId, framePxToFixture(gestureFrame.current, containerSize));
                      }
                      setTimeout(() => {
                        didInteractRef.current = false;
                      }, 0);
                    }}
                    onRotateEnd={() => {
                      if (didMoveRef.current) {
                        updateFixture(selectedFixtureId, framePxToFixture(gestureFrame.current, containerSize));
                      }
                      setTimeout(() => {
                        didInteractRef.current = false;
                      }, 0);
                    }}
                  />
                )}

                {/* Room Labels (read-only, placed in previous step) */}
                {config.roomLabels.map((label) => (
                  <div
                    key={label.id}
                    className="absolute pointer-events-none"
                    style={{
                      left: `${label.x * 100}%`,
                      top: `${label.y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 5,
                    }}
                  >
                    <div className="rounded-full bg-white/80 border border-border/50 px-2.5 py-1 text-[10px] font-medium text-muted-foreground backdrop-blur-sm">
                      {label.label}
                    </div>
                  </div>
                ))}

              </div>
            </CardContent>
          </Card>

        </div>

        {/* Right Panel: Configuration */}
        <div className="lg:col-span-2 space-y-4">
          {/* Fixture Placement (Feste Einbauten) */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold tracking-tight">
                Feste Einbauten
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {(useSymbols
                  ? FIXTURE_PALETTE.filter((p) => ['shower', 'bathtub', 'sink', 'toilet'].includes(p.type))
                  : FIXTURE_PALETTE
                ).map((item) => {
                  const Icon = item.icon;
                  const count = (config.fixtures || []).filter(
                    (f) => f.type === item.type
                  ).length;
                  return (
                    <button
                      key={item.type}
                      onClick={() => addFixture(item.type)}
                      className="flex items-center gap-2 rounded-lg border border-border p-2.5 text-left transition-all hover:border-primary/30 hover:bg-muted/50"
                    >
                      <div
                        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: item.color.replace(/[\d.]+\)$/, '0.15)'),
                          color: item.color.replace(/[\d.]+\)$/, '1)'),
                        }}
                      >
                        {useSymbols ? (
                          <FixtureSymbolSvg symbolKey={item.type as FixtureSymbolKey} className="w-6 h-6" />
                        ) : (
                          <Icon className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-medium">{item.displayLabel}</div>
                      </div>
                      {count > 0 && (
                        <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                          {count}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
              {(config.fixtures?.length || 0) > 0 && (
                <p className="text-[10px] text-muted-foreground">
                  {config.fixtures!.length} Einbauten platziert — verschieben und skalieren
                  Sie die Blöcke auf dem Grundriss.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Ausgewählter Einbau (Properties Panel, nur useSymbols-Zweig) — Größen-
              Slider + Rotation geben präzise Kontrolle unabhängig vom winzigen
              On-Canvas-Objekt (z.B. WC). */}
          {useSymbols && selectedFixture && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold tracking-tight">
                    Ausgewählter Einbau
                  </h3>
                  <Badge variant="secondary" className="text-[10px] px-2 py-0 font-normal">
                    {selectedFixturePalette?.displayLabel ?? selectedFixture.label}
                  </Badge>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <label className="text-xs text-muted-foreground">Breite</label>
                    <span className="text-xs font-mono text-foreground">
                      {Math.round(selectedFixture.width * 100)}%
                    </span>
                  </div>
                  <Slider
                    min={0.02}
                    max={0.45}
                    step={0.005}
                    value={[selectedFixture.width]}
                    onValueChange={([v]) => updateFixture(selectedFixture.id, { width: v })}
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <label className="text-xs text-muted-foreground">Höhe</label>
                    <span className="text-xs font-mono text-foreground">
                      {Math.round(selectedFixture.height * 100)}%
                    </span>
                  </div>
                  <Slider
                    min={0.02}
                    max={0.45}
                    step={0.005}
                    value={[selectedFixture.height]}
                    onValueChange={([v]) => updateFixture(selectedFixture.id, { height: v })}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Rotation</label>
                  <RotationControl
                    value={selectedFixture.rotation ?? 0}
                    onChange={(deg) => updateFixture(selectedFixture.id, { rotation: deg })}
                  />
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-destructive hover:text-destructive"
                  onClick={() => {
                    removeFixture(selectedFixture.id);
                    setSelectedFixtureId(null);
                  }}
                >
                  <X className="w-3.5 h-3.5 mr-1.5" />
                  Löschen
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Per-Area Flooring */}
          <Card>
            <CardContent className="p-4 space-y-1">
              <h3 className="text-sm font-semibold tracking-tight mb-2">
                Bodenbelag nach Bereich
              </h3>
              <Accordion type="single" collapsible className="w-full">
                {AREA_GROUPS.map((group) => {
                  const currentFlooring = getAreaFlooring(group.id);
                  return (
                    <AccordionItem key={group.id} value={group.id} className="border-b last:border-b-0">
                      <AccordionTrigger className="py-2.5 hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-2">
                          <span className="text-sm">{group.label}</span>
                          <Badge variant="secondary" className="text-[10px] px-2 py-0 font-normal">
                            {FLOORING_LABEL_MAP[currentFlooring]}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-1 gap-1.5 pt-1">
                          {FLOORING_OPTIONS.filter((o) => AREA_FLOORING_OPTIONS[group.id].includes(o.id)).map((option) => {
                            const Icon = option.icon;
                            const isSelected = currentFlooring === option.id;
                            return (
                              <button
                                key={option.id}
                                onClick={() => setAreaFlooring(group.id, option.id)}
                                className={`
                                  flex items-center gap-2.5 rounded-lg border p-2.5 text-left transition-all
                                  ${
                                    isSelected
                                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                                      : 'border-border hover:border-primary/30 hover:bg-muted/50'
                                  }
                                `}
                              >
                                <div
                                  className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                                    isSelected
                                      ? 'bg-primary/10 text-primary'
                                      : 'bg-muted text-muted-foreground'
                                  }`}
                                >
                                  <Icon className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-medium">{option.label}</div>
                                  <div className="text-[10px] text-muted-foreground">
                                    {option.description}
                                  </div>
                                </div>
                                {isSelected && (
                                  <Check className="w-3.5 h-3.5 text-primary shrink-0 ml-auto" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>

          {/* Text Handling */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold tracking-tight">
                Beschriftung
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { id: 'remove' as FloorplanTextHandling, label: 'Entfernen', desc: 'Maße & Text entfernen' },
                    { id: 'keep' as FloorplanTextHandling, label: 'Beibehalten', desc: 'Alle Texte beibehalten' },
                  ] as const
                ).map((option) => {
                  const isSelected = config.textHandling === option.id;
                  return (
                    <button
                      key={option.id}
                      onClick={() => updateConfig({ textHandling: option.id })}
                      className={`
                        rounded-lg border p-3 text-center transition-all
                        ${
                          isSelected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                            : 'border-border hover:border-primary/30 hover:bg-muted/50'
                        }
                      `}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {option.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Pricing Info */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="p-4">
              <div className="space-y-1.5 text-sm">
                <p className="text-muted-foreground">
                  1 Grundriss = 2D + 3D zusammen. Wird bei der ersten Generierung
                  verbraucht — alle Anpassungen und die 3D-Ansicht sind danach
                  kostenlos.
                </p>
                <div className="flex items-center justify-between pt-1 border-t">
                  <span className="text-muted-foreground">Dein Kontingent:</span>
                  <span
                    className={`font-semibold ${
                      hasQuota ? 'text-emerald-600' : 'text-destructive'
                    }`}
                  >
                    {floorplanUnits} Grundriss{floorplanUnits !== 1 ? 'e' : ''}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Error */}
      {floorplanState.generation2dError && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">
            {floorplanState.generation2dError}
          </CardContent>
        </Card>
      )}

      {/* Admin-only: in-process Prompt-Tuning-Panel für den Vision-Analyst */}
      {isAdmin && analyzeSourcePath && onGenerateWithPrompt && (
        <FloorplanAnalyzePanel
          resolveImagePath={async () => {
            // Analyse muss dasselbe (label-gebrannte) Bild sehen wie die echte 2D-Generierung.
            // Spiegelt useFloorplanWorkflow.handleGenerate2D: Labels/Fixtures ins Bild brennen.
            const cfg = floorplanState.config;
            const hasFixtures = !!cfg.fixtures && cfg.fixtures.length > 0;
            const hasLabels = !cfg.roomsLabeled && cfg.roomLabels.length > 0;
            if ((!hasFixtures && !hasLabels) || !displayImageUrl) return analyzeSourcePath;
            const { burnAnnotationsIntoImage } = await import('@/lib/imageUtils');
            const { uploadAnnotatedBlob } = await import('@/services/floorplanService');
            const blob = await burnAnnotationsIntoImage(
              displayImageUrl,
              cfg.fixtures || [],
              hasLabels ? cfg.roomLabels : [],
              { useSymbols },
            );
            return await uploadAnnotatedBlob(projectId, floorplanState.imageDbId || '', blob);
          }}
          projectId={projectId}
          onGenerateWithPrompt={onGenerateWithPrompt}
        />
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        <div className="flex items-center gap-3">
          {!hasQuota && (
            <Button variant="outline" size="sm" onClick={onBuyCredits}>
              <Coins className="w-4 h-4 mr-2" />
              Grundriss-Kontingent kaufen
            </Button>
          )}
          {has2d && (
            <Button variant="outline" onClick={onNavigateToReview} className="gap-2">
              Weiter: Ergebnis
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || (consumesUnit && floorplanUnits < 1)}
            className="gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generiert...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                {has2d
                  ? '2D neu generieren (kostenlos)'
                  : '2D generieren (1 Grundriss)'}
                {!has2d && <ArrowRight className="w-4 h-4" />}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
